import { BasketSections, RequirementTypes } from '~/lib/types/enums';
import useQuoteRequirements from './use-quote-requirements';
import { useMemo } from 'react';
import useQuote from './use-quote';
import { doesQuoteForceCollectionAddressSelection } from '~/lib/quotes';
import useBasketSection from './use-basket-section';
import { QuoteAddress, QuoteExtras } from '~/lib/types/basket-types';
import { AddressCustomsTaxFields, BusinessCompanyStatuses } from '~/lib/types/customs';
import { AddRuleExamples, MapRequirement, SwapRequirementsFromSelectedExtras } from '~/lib/utils/requirements';

export function useAddressRequirements(type: RequirementTypes.ORIGIN | RequirementTypes.DESTINATION) {
  const { requirements, loading, countries, requiresCustoms } = useQuoteRequirements();
  const { quote } = useQuote();
  const extrasSection = useBasketSection<QuoteExtras>({ name: BasketSections.EXTRAS });
  const originSection = useBasketSection<QuoteAddress>({ name: BasketSections.ORIGIN });
  const destinationSection = useBasketSection<QuoteAddress>({ name: BasketSections.DESTINATION });

  const forceAddressSelection = useMemo(
    () => type === RequirementTypes.ORIGIN && doesQuoteForceCollectionAddressSelection(quote),
    [type, quote]
  );

  const requirementsFromSelectedExtras = useMemo(
    () =>
      (
        quote?.availableExtras
          ?.filter((x) => x.requirements?.length && extrasSection.data.extras.some((e) => e.key === x.key))
          .flatMap((x) => x.requirements) || []
      ).filter((x) => !!x),
    [quote, extrasSection]
  );

  const unfilteredRequirementKeys = useMemo(() => {
    return (
      requirements[type]?.rules
        ?.filter((x) => AddressCustomsTaxFields.includes(x.key))
        ?.map((x) => ({
          key: x.key,
          name: x.name
        })) || []
    );
  }, [requirements, type]);

  const requirement = useMemo(() => {
    // Main Address Requirements
    const output = {
      ...requirements[type],
      rules: requirements[type]?.rules.map((x) => ({ ...x }))
    };

    // Hide optional customs fields when the user isn't a business
    const hide = ['ShopId', 'shopId'].concat(
      BusinessCompanyStatuses.includes(
        type === RequirementTypes.ORIGIN
          ? originSection.data?.companyStatus || ''
          : destinationSection.data?.companyStatus || ''
      )
        ? []
        : output.rules?.filter((r) => !r.required && AddressCustomsTaxFields.includes(r.key)).map((x) => x.key) || []
    );

    // Add the data required to dermine if a field is required/shown
    const data: { [key: string]: QuoteAddress } = {
      [RequirementTypes.ORIGIN]: originSection.data,
      [RequirementTypes.DESTINATION]: destinationSection.data
    };

    // Swap out some requirements based on selected extras. Eg Phone ->  Mobile when SMS is selected.
    SwapRequirementsFromSelectedExtras(type, requirementsFromSelectedExtras, output);

    // Add some examples for address rules where they don't exist on the api
    AddRuleExamples(
      output,
      type,
      type === RequirementTypes.ORIGIN ? originSection.data?.country || '' : destinationSection.data?.country || ''
    );

    return {
      ...output,
      rules: output.rules?.map((x) => MapRequirement(x, data, type)).filter((x) => !hide.includes(x.key)) || []
    };
  }, [type, requirements, requirementsFromSelectedExtras, originSection, destinationSection]);

  return {
    requirements: requirement,
    loading,
    countries: countries[type],
    requiresCustoms,
    forceAddressSelection,
    unfilteredRequirementKeys
  };
}
